import { useEffect, useRef } from "react";

import { CircleCheck, CircleX } from "lucide-react";
import { useTranslation } from "react-i18next";

import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { BUTTON_TYPES } from "@/components_v2/Button/constants";
import { useVerifyApplicationMutation } from "@/redux/service";

import { EmailVerificationProps } from "./types";

import "./styles.css";

const EmailVerification = ({ token }: EmailVerificationProps) => {
  const { t } = useTranslation();
  const hasAttempted = useRef(false);

  const [verifyApplication, { isLoading, isSuccess, isError }] =
    useVerifyApplicationMutation();

  useEffect(() => {
    if (!token || hasAttempted.current) {
      return;
    }
    hasAttempted.current = true;
    verifyApplication({ token });
  }, [token, verifyApplication]);

  if (token && (isLoading || (!isSuccess && !isError))) {
    return (
      <div className="flex flex-col items-center gap-400">
        <BaseSpinner size="large" />
        <div className="flex flex-col items-center gap-200 text-center">
          <h2 className="email-verification__title header-m-medium">
            {t("applications.labels.verifyingEmailTitle")}
          </h2>
          <p className="email-verification__description body-m-regular">
            {t("applications.labels.verifyingEmailDescription")}
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center gap-400">
        <div className="email-verification__success-icon-container flex items-center justify-center">
          <CircleCheck
            className="email-verification__success-icon"
            aria-hidden="true"
          />
        </div>

        <div className="flex flex-col items-center gap-200 text-center">
          <h2 className="email-verification__title header-m-medium">
            {t("applications.labels.emailVerifiedTitle")}
          </h2>
          <p className="email-verification__description body-m-regular">
            {t("applications.labels.emailVerifiedDescription")}
          </p>
        </div>

        <BaseButton isFullWidth type={BUTTON_TYPES.LINK} to="/login">
          {t("applications.labels.backToCommons")}
        </BaseButton>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-400">
      <div className="email-verification__error-icon-container flex items-center justify-center">
        <CircleX
          className="email-verification__error-icon"
          aria-hidden="true"
        />
      </div>

      <div className="flex flex-col items-center gap-200 text-center">
        <h2 className="email-verification__title header-m-medium">
          {t("applications.labels.emailVerificationFailedTitle")}
        </h2>
        <p className="email-verification__description body-m-regular">
          {t("applications.labels.emailVerificationFailedDescription")}
        </p>
      </div>

      <BaseButton isFullWidth type={BUTTON_TYPES.LINK} to="/apply">
        {t("applications.labels.applyAgain")}
      </BaseButton>
    </div>
  );
};

export default EmailVerification;
